/**
 * Início: a saudação com a busca e o "Criar sala", o destaque da última mesa ("continuar de onde
 * parou"), as suas mesas em abas (recentes, minhas, favoritas: só aqui, o Explorar não as tem) e o
 * convite para explorar pelos sistemas mais jogados. Na tela estreita (max-width 760 px) o destaque
 * põe a capa em cima e os cartões das mesas ficam um por linha.
 */
import { useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n/index.js';
import type { ExploreTab, RoomCard } from '../shared.js';
import { useApp } from '../store.js';
import { useNow } from '../social/FriendChatWindow.js';
import { seenAgo } from '../social/friendsModel.js';
import { Button } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { PageHeader } from '../ui/PageHeader.js';
import { SearchBox } from '../ui/SearchBox.js';
import { toast } from '../ui/Toaster.js';
import { CreateRoomPopup } from './CreateRoomPopup.js';
import { RoomOverviewPopup } from './RoomOverviewPopup.js';
import { EmptyNote, OnlineLine, RoomMark, RoomTile, coverOf, roomLink, systemOf } from './RoomTile.js';
import { enterAction, greetingKey, topSystems, type EnterAction } from './homeModel.js';
import { useExplore, withoutAdult } from './state.js';
import './explore.css';

type HomeTab = Extract<ExploreTab, 'recent' | 'mine' | 'favorites'>;
const TABS: readonly HomeTab[] = ['recent', 'mine', 'favorites'];
/** cartões por aba antes do "Mostrar todas"; o último lugar da grade é o "Criar nova mesa" */
const PER_TAB = 11;

type HomeTarget = { kind: 'explore'; tab?: ExploreTab; query?: string; system?: string } | { kind: 'room'; roomId: string };

export function HomeState({ onNavigate }: { onNavigate: (target: HomeTarget) => void }) {
  const t = useT();
  const me = useApp((s) => s.user);
  const hideAdult = useApp((s) => s.settings.hideAdultRooms);
  const allRooms = useApp((s) => s.rooms);
  const openRooms = useApp((s) => s.openRooms);
  const activeRoomId = useApp((s) => s.activeRoomId);
  const joinRoom = useApp((s) => s.joinRoom);
  const lists = useExplore((s) => s.tabs);
  const load = useExplore((s) => s.load);
  const [tab, setTab] = useState<HomeTab>('recent');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<RoomCard | null>(null);
  const [creating, setCreating] = useState(false);
  // as abas do Início são as únicas listas das suas mesas: "Mostrar todas" abre a lista inteira
  const [all, setAll] = useState(false);

  useEffect(() => {
    for (const id of TABS) void load(id);
  }, [load]);

  const rooms = (id: HomeTab) => withoutAdult(lists[id]?.rooms ?? [], hideAdult, me?.id);
  // a mais recente vai para o destaque; as Recentes mostram as outras
  const hero = rooms('recent')[0] ?? null;
  const byTab: Record<HomeTab, RoomCard[]> = {
    recent: rooms('recent').slice(1),
    mine: rooms('mine'),
    favorites: rooms('favorites'),
  };
  const shown = byTab[tab];
  // a única recente já está no destaque: não é "nenhuma mesa recente"
  const empty = !shown.length && !(lists[tab]?.loading ?? true) && !(tab === 'recent' && hero);
  const systems = useMemo(() => topSystems(withoutAdult(allRooms, hideAdult, me?.id)), [allRooms, hideAdult, me?.id]);
  const openRoomIds = openRooms.map((room) => room.roomId);
  const actionOf = (card: RoomCard) => enterAction(card, me?.id, openRoomIds);

  const enter = (card: RoomCard) => {
    if (actionOf(card) === 'password') setOpen(card);
    else if (card.id === activeRoomId) onNavigate({ kind: 'room', roomId: card.id });
    else joinRoom(card.id);
  };
  const search = (words: string) => onNavigate({ kind: 'explore', tab: 'search', query: words.trim() });
  const visible = all ? shown : shown.slice(0, PER_TAB);
  // um minuto basta para o "há 5 min" e para a saudação trocarem sozinhos
  const now = useNow(60_000);

  return (
    <div className="rd-home">
      <PageHeader
        className="rd-home__head"
        title={t(greetingKey(new Date(now).getHours()), { name: me?.displayName ?? '' })}
        subtitle={t('home.tagline')}
        actions={
          <div className="rd-home__tools" role="search">
            <SearchBox className="rd-home__search" value={query} onChange={setQuery} onSubmit={search} placeholder={t('home.search')} />
            <Button variant="action" icon="plus" onClick={() => setCreating(true)}>
              {t('home.create')}
            </Button>
          </div>
        }
      />

      {hero ? <HomeHero card={hero} action={actionOf(hero)} now={now} onEnter={() => enter(hero)} /> : null}

      <div className="rd-home__quick">
        <Button variant="action" size="lg" icon="plus" onClick={() => setCreating(true)}>
          {t('home.create')}
        </Button>
        <Button variant="secondary" size="lg" icon="search" onClick={() => onNavigate({ kind: 'explore' })}>
          {t('home.explore.short')}
        </Button>
      </div>

      <section className="rd-home__rooms" aria-labelledby="rd-home-rooms">
        <header className="rd-home__roomsHead">
          <h2 id="rd-home-rooms" className="rd-home__h2">
            {t('home.rooms.title')}
          </h2>
          <button type="button" className="rd-home__more" onClick={() => onNavigate({ kind: 'explore' })}>
            {t('home.rooms.seeAll')}
            <Icon name="arrowRight" size={18} />
          </button>
        </header>
        <div className="rd-homeTabs" role="tablist" aria-label={t('home.rooms.filter')}>
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`rd-homeTabs__tab${tab === id ? ' is-on' : ''}`}
              onClick={() => {
                setTab(id);
                setAll(false);
              }}
            >
              {t(`home.group.${id}`)}
              <span className="rd-homeTabs__count">{byTab[id].length}</span>
            </button>
          ))}
        </div>
        <div role="tabpanel" aria-label={t(`home.group.${tab}`)}>
          {shown.length || tab !== 'favorites' ? (
            <div className="rd-roomGrid">
              {visible.map((card) => {
                const visit = seenAgo(card.lastAccessAt, now, 'home.visit');
                return (
                  <RoomTile
                    key={card.id}
                    card={card}
                    label={t('home.card.enter', { name: card.name })}
                    onActivate={() => enter(card)}
                    onOpen={setOpen}
                    status={card.onlineCount ? undefined : <span className="rd-roomTile__playing">{t(visit.key, visit.params)}</span>}
                  />
                );
              })}
              {empty ? <EmptyNote icon={tab === 'recent' ? 'clock' : 'die'} title={t(`home.empty.${tab}`)} text={t(`explore.empty.hint.${tab}`)} /> : null}
              <button type="button" className="rd-homeNew" onClick={() => setCreating(true)}>
                <span className="rd-homeNew__icon" aria-hidden="true">
                  <Icon name="plus" size={24} />
                </span>
                <span className="rd-homeNew__body">
                  <span className="rd-homeNew__title">{t('home.newRoom.title')}</span>
                  <span className="rd-homeNew__text">{t('home.newRoom.text')}</span>
                </span>
              </button>
            </div>
          ) : null}
          {shown.length > visible.length ? (
            <Button variant="secondary" className="rd-home__all" onClick={() => setAll(true)}>
              {t('home.rooms.all', { n: shown.length })}
            </Button>
          ) : null}
          {shown.length || tab !== 'favorites' ? null : (
            <EmptyNote icon="star" title={t('home.empty.favorites')} text={t('home.empty.favorites.text')} wide />
          )}
        </div>
      </section>

      <section className="rd-homeExplore" aria-labelledby="rd-home-explore">
        <div className="rd-homeExplore__body">
          <h2 id="rd-home-explore" className="rd-home__h2 rd-homeExplore__title">
            {t('home.explore.title')}
          </h2>
          <p className="rd-homeExplore__text">{t('home.explore.text')}</p>
          {systems.length ? (
            <ul className="rd-homeExplore__systems" aria-label={t('home.explore.systems')}>
              {systems.map((system) => (
                <li key={system}>
                  <button type="button" className="rd-homeChip" onClick={() => onNavigate({ kind: 'explore', tab: 'search', system })}>
                    {system}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <Button variant="secondary" icon="search" className="rd-homeExplore__go" onClick={() => onNavigate({ kind: 'explore' })}>
          {t('home.explore.go')}
        </Button>
      </section>

      {open ? <RoomOverviewPopup card={open} onClose={() => setOpen(null)} /> : null}
      {creating ? <CreateRoomPopup onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function HomeHero({ card, action, now, onEnter }: { card: RoomCard; action: EnterAction; now: number; onEnter: () => void }) {
  const t = useT();
  const system = systemOf(card);
  const seen = seenAgo(card.lastAccessAt, now, 'home.hero.seen');
  const invite = () => {
    void navigator.clipboard?.writeText(roomLink(card.id)).then(() => toast(t('home.hero.invited'), 'success'));
  };
  return (
    <section className="rd-homeHero" aria-labelledby="rd-home-hero">
      <div className="rd-homeHero__body">
        <h2 id="rd-home-hero" className="rd-homeHero__name">
          {card.name}
        </h2>
        <div className="rd-homeHero__facts">
          {system ? <span className="rd-homeHero__chip">{system}</span> : null}
          {card.hasPassword ? (
            <span className="rd-homeHero__chip">
              <Icon name="lock" size={14} />
              {t('explore.card.password')}
            </span>
          ) : null}
          <span className="rd-homeHero__seen">{t(seen.key, seen.params)}</span>
        </div>
        <p className="rd-homeHero__presence">
          <OnlineLine card={card} className="rd-homeHero__online" />
          {card.ownerOnline ? <span> · {t('home.hero.gm')}</span> : null}
        </p>
        <div className="rd-homeHero__actions">
          <button type="button" className="rd-btn rd-btn--action rd-btn--lg rd-homeHero__enter" onClick={onEnter}>
            {t(action === 'back' ? 'home.hero.back' : 'home.hero.enter')}
            <Icon name="arrowRight" size={18} />
          </button>
          <button type="button" className="rd-btn rd-btn--secondary rd-btn--lg rd-homeHero__invite" aria-label={t('home.hero.invite')} onClick={invite}>
            <Icon name="userPlus" size={18} />
            <span className="rd-homeHero__inviteText">{t('home.hero.invite')}</span>
          </button>
        </div>
      </div>
      <div className="rd-homeHero__cover" style={{ backgroundImage: coverOf(card) }} aria-hidden="true">
        <RoomMark card={card} className="rd-homeHero__mark" />
      </div>
    </section>
  );
}
